(ns ui.components.combo-box.view
  (:require
   [clojure.string :as string]
   [frontend.utils :as fu]
   ["react-window" :refer (FixedSizeList)]
   [reagent.core :as r]
   [ui.components.radix-ui.popover.view :refer
    [popover-close
     popover-content
     popover-portal
     popover-root
     popover-trigger]]))

(defn view
  [{:keys [options search-visible-threshold on-change]}
   _trigger-component]
  (let [open-state (r/atom false)
        search-term-state (r/atom "")
        input-ref-state (r/atom nil)
        close-btn-ref-state (r/atom nil)
        focused-option-idx-state (r/atom 0)
        visible-options-state (r/atom [])
        visible-start-idx-state (r/atom 0)
        visible-stop-idx-state (r/atom 0)
        list-ref-state (r/atom nil)
        mouse-disabled-state (r/atom false)
        search-input-visible? (if (number? search-visible-threshold)
                                (<= search-visible-threshold (count options))
                                true)

        scroll-to-item (fn [index]
                         (when-let [list-ref @list-ref-state]
                           (let [scroll-fn (aget list-ref "scrollToItem")]
                             (.call scroll-fn list-ref index 0))))
        set-focused-option-index (fn [index]
                                   (reset! focused-option-idx-state index)
                                   (let [visible-start-idx @visible-start-idx-state
                                         visible-stop-idx @visible-stop-idx-state]
                                     (when (or (< (dec visible-stop-idx) index)
                                               (< index (inc visible-start-idx)))
                                       (scroll-to-item index))))
        select-option (fn [option-value]
                        (reset! search-term-state "")
                        (reset! focused-option-idx-state 0)
                        (reset! visible-options-state [])
                        (reset! open-state false)
                        (on-change option-value))
        keyboard-listener (fn [e]
                            (when @open-state
                              (let [event-key (.-key e)
                                    focused-option-idx @focused-option-idx-state
                                    visible-options @visible-options-state
                                    option-count (count visible-options)]
                                (case event-key
                                  "ArrowDown"
                                  (do
                                    (.preventDefault e)
                                    (when (< focused-option-idx (dec option-count))
                                      (reset! mouse-disabled-state true)
                                      (set-focused-option-index (inc focused-option-idx))))

                                  "ArrowUp"
                                  (do
                                    (.preventDefault e)
                                    (when (< 0 focused-option-idx)
                                      (reset! mouse-disabled-state true)
                                      (set-focused-option-index (dec focused-option-idx))))

                                  "Enter"
                                  (when (and (< 0 option-count)
                                             (<= focused-option-idx (dec option-count)))
                                    (let [selected-option (nth visible-options focused-option-idx)
                                          value (:value selected-option)]
                                      (select-option value)))

                                  nil))))
        mouse-move-allower (fn []
                             (reset! mouse-disabled-state false))]
    (r/create-class
     {:component-did-mount    (fn [])
      :component-will-unmount (fn [])
      :reagent-render
      (fn [{:keys [placeholder options]}
           trigger-component]
        (let [open? @open-state
              search-term @search-term-state
              search-term-lc (string/lower-case search-term)
              visible-options (->> options
                                   (filter :label)
                                   (sort-by :label)
                                   (filter (fn [{:keys [label]}]
                                             (string/includes? (string/lower-case label) search-term-lc))))
              visible-option-count (count visible-options)
              list-item-height 36
              list-height (min (max list-item-height (* visible-option-count list-item-height)) 250)]
          (reset! visible-options-state visible-options)
          @focused-option-idx-state

          [popover-root
           {:open         open?
            :onOpenChange (fn [open?]
                            (reset! open-state open?)
                            (if open?
                              (do
                                (.addEventListener js/document "keydown" keyboard-listener)
                                (.addEventListener js/document "mousemove" mouse-move-allower))
                              (do
                                (set-focused-option-index 0)
                                (.removeEventListener js/document "keydown" keyboard-listener)
                                (.removeEventListener js/document "mousemove" mouse-move-allower))))}
           ;; `asChild` with a plain wrapper div makes the div the trigger
           ;; element, so callers can pass a real <button> as the trigger
           ;; without nesting <button> inside radix's own <button> (which
           ;; `asChild false` would do, tripping validateDOMNesting).
           [popover-trigger
            {:asChild true}
            [:div {:class "appearance-none p-0 m-0"}
             trigger-component]]

           [popover-portal
            {}
            [popover-content
             {:class "w-64 relative z-[4000]
                      text-base-light-text
                      border border-solid border-base-light-border-paler
                      bg-base-light
                      rounded shadow overflow-y-auto"
              :align :start}
             [popover-close
              {:class     "absolute m-0 p-0 w-0 h-0"
               :tab-index "-1"
               :ref       (fn [ref]
                            (reset! close-btn-ref-state ref))}]
             [:div
              (when search-input-visible?
                [:div
                 {:class "relative p-3"}
                 [:i.fal.fa-search
                  {:class "fal fa-search absolute top-2/4 -translate-y-2/4 right-5"}]
                 [:input
                  {:ref         (fn [ref]
                                  (reset! input-ref-state ref))
                   :value       search-term
                   :on-change   (fn [e]
                                  (reset! search-term-state (.. e -target -value))
                                  (reset! focused-option-idx-state 0)
                                  (scroll-to-item 0)
                                  (reset! mouse-disabled-state true))
                   :placeholder (or placeholder "Search...")
                   :class       "input-area py-2 px-3 w-full text-input-light-text placeholder:text-input-light-text-paler outline-0 border border-solid border-input-light-border-paler hover:border-input-light-border focus:ring-1 focus:ring-inset focus:ring-input-light-border focus-visible:ring-input-light-border rounded bg-base-light"}]])
              [:div
               {:class "py-2"}
               (if (< 0 visible-option-count)
                 [:> FixedSizeList
                  {:height          list-height
                   :itemCount       visible-option-count
                   :itemSize        list-item-height
                   :ref             (fn [dom-node]
                                      (reset! list-ref-state dom-node))
                   :onItemsRendered (fn [e]
                                      (let [_overscan-start-idx (aget e "overscanStartIndex")
                                            _overscan-stop-idx (aget e "overscanStopIndex")
                                            visible-start-idx (aget e "visibleStartIndex")
                                            visible-stop-idx (aget e "visibleStopIndex")]
                                        (reset! visible-start-idx-state visible-start-idx)
                                        (reset! visible-stop-idx-state visible-stop-idx)))}
                  (fn [item-js-props]
                    (let [style (.-style item-js-props)
                          index (.-index item-js-props)
                          option (nth visible-options index)
                          option-value (:value option)
                          option-label (:label option)
                          focused? (= index @focused-option-idx-state)]
                      (r/as-element [:div
                                     {:style style}
                                     [:div
                                      {:class          (fu/cs "p-2 mx-3 rounded"
                                                              (when focused?
                                                                "bg-complement-regular"))

                                       :on-click       (fn []
                                                         (on-change option-value)
                                                         (reset! open-state false))
                                       :on-mouse-enter (fn []
                                                         (when-not @mouse-disabled-state
                                                           (reset! focused-option-idx-state index)))}
                                      [:span
                                       {:class "block text-ellipsis overflow-hidden"}
                                       option-label]]])))]
                 [:div
                  {:class "px-2 mx-2 h-7 whitespace-nowrap flex items-center rounded overflow-hidden focus:bg-base-regular"}
                  [:span "No options."]])]]]]]))})))
